import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from 'src/environments/environment';

// ENG-5981: store owner agent (menu, employees, printers, pause, sales) — one chat.
export interface AgentAction { tool: string; args: any; result: any; undo_id?: string; undoState?: 'running' | 'done' | 'failed'; undoNote?: string; }
export interface ChangeSummary {
  title: string;
  target?: string | null;
  changes: { field: string; from: any; to: any }[];
  note?: string | null;
}
export interface AgentPending { pending_id: string; tool: string; domain: string; args: any; preview: any; summary?: ChangeSummary; undo_note?: string | null; }
export interface AgentReply {
  message: string;
  actions: AgentAction[];
  pending: AgentPending[];
  dry_run: boolean;
  conversation_id: string;
}

@Injectable({providedIn: 'root'})
export class StoreAgentService {
  private base = `${environment.apiUrl}/backend/v2/agent`;

  constructor(private http: HttpClient) {}

  /** posPin: on the POS, the PIN of the person using the assistant (sent on every call). */
  chat(storeId: string | number, body: { message: string; conversation_id?: string; dry_run?: boolean }, posPin?: string) {
    return this.http.post<AgentReply>(`${this.base}/chat/?store=${storeId}`, {...body, ...this.who(posPin)});
  }

  confirm(storeId: string | number, pendingId: string, posPin?: string) {
    return this.http.post<{ done: boolean; result: any; undo_id?: string | null }>(
      `${this.base}/confirm/?store=${storeId}`, {pending_id: pendingId, ...this.who(posPin)});
  }

  private who(posPin?: string) {
    return posPin ? {channel: 'pos', pos_pin: posPin} : {channel: 'console'};
  }

  undo(storeId: string | number, undoId: string, posPin?: string) {
    return this.http.post<{ done: boolean; result: any }>(
      `${this.base}/undo/?store=${storeId}`, {undo_id: undoId, ...this.who(posPin)});
  }

  sendPhoneLink(storeId: string | number) {
    return this.http.post<{ id: number; url: string; sent_to: string; sms_error: string | null; expires_at: string }>(
      `${this.base}/links/?store=${storeId}`, {send_sms: true});
  }

  // T8: manager access (Master Admin only)
  grants(storeId: string | number) {
    return this.http.get<{ grants: { user_id: number; name: string; domains: string[] }[];
      staff: { user_id: number; name: string; role: string | null; has_permission?: boolean }[]; domains: string[] }>(
      `${this.base}/grants/?store=${storeId}&with_staff=1`);
  }

  saveGrant(storeId: string | number, userId: number, domains: string[]) {
    return this.http.post<any>(`${this.base}/grants/?store=${storeId}`, {user_id: userId, domains});
  }

  removeGrant(storeId: string | number, userId: number) {
    return this.http.delete<any>(`${this.base}/grants/${userId}/?store=${storeId}`);
  }

  // T9: phone links (Master Admin only)
  links(storeId: string | number) {
    return this.http.get<{ id: number; user: string; sent_to: string; created_at: string; expires_at: string;
      revoked: boolean; device_bound: boolean; last_used_at: string | null }[]>(`${this.base}/links/?store=${storeId}`);
  }

  revokeLink(storeId: string | number, linkId: number) {
    return this.http.delete<any>(`${this.base}/links/${linkId}/?store=${storeId}`);
  }

  /** The chat so far (text only), to show it again after a reload. Kept 24 h on the server. */
  history(storeId: string | number, conversationId: string) {
    return this.http.get<{ conversation_id: string; messages: { role: 'user' | 'assistant'; text: string }[] }>(
      `${this.base}/history/?store=${storeId}&conversation_id=${encodeURIComponent(conversationId)}`);
  }

  logs(storeId: string | number, limit = 100) {
    return this.http.get<any[]>(`${this.base}/logs/?store=${storeId}&limit=${limit}`);
  }
}
